import React from 'react';
import { Link } from 'react-router-dom';
import { companyInfo, trustBadges } from '../../data/mock';
import { Phone, Mail, Facebook, Linkedin, ShieldCheck, Lock, FileCheck, Star } from 'lucide-react';

const iconMap = {
  ShieldCheck,
  Lock,
  FileCheck,
  Star
};

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white">
      {/* Partner Banks Section */}
      <div className="border-b border-slate-700">
        <div className="container mx-auto px-4 py-10">
          <h3 className="text-center text-sm font-medium text-slate-400 mb-8">Unsere Partnerbanken</h3>
          <div className="flex justify-center items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_kredithilfe-1/artifacts/ftndzr2v_%7B93C3A8D5-F2C7-449F-8AE9-E6F9E88C9910%7D.png" 
              alt="Partnerbanken" 
              className="max-w-full h-auto object-contain opacity-90 hover:opacity-100 transition-opacity"
              style={{ maxHeight: '60px', filter: 'brightness(1.1)' }}
            />
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="border-b border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-wrap justify-center items-center gap-8">
            {trustBadges.map((badge) => {
              const IconComponent = iconMap[badge.icon];
              return (
                <div key={badge.id} className="flex items-center gap-2 text-slate-300">
                  {IconComponent && <IconComponent className="w-5 h-5 text-blue-400" />}
                  <span className="text-sm font-medium">{badge.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_loan-compare-2/artifacts/cfzo1i0k_%7B2DFB527A-B0B8-42B3-A709-3F13FA3C1A27%7D.png" 
                alt={companyInfo.name}
                className="w-10 h-10 object-contain"
              />
              <span className="text-lg font-bold">{companyInfo.name}</span>
            </div>
            <p className="text-slate-400 text-sm mb-4">{companyInfo.tagline}</p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Schnellzugriff</h4>
            <ul className="space-y-2">
              {[
                { href: '/vergleich', label: 'Finanzierungsvergleich' },
                { href: '/anfrage', label: 'Anfrage stellen' },
                { href: '/beratung', label: 'Beratung' },
                { href: '/ueber-uns', label: 'Über uns' },
                { href: '/faq', label: 'FAQ' },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-blue-400 text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Financing Types */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Finanzierungsarten</h4>
            <ul className="space-y-2">
              {[
                'Immobilienfinanzierung',
                'Kreditvergleich',
                'Umschuldung',
                'Ratenkredit',
                'Geschäftskredit',
              ].map((type) => (
                <li key={type}>
                  <Link
                    to="/vergleich"
                    className="text-slate-400 hover:text-blue-400 text-sm transition-colors"
                  >
                    {type}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Kontakt</h4>
            <div className="space-y-3">
              <a
                href={`tel:${companyInfo.contact.phone}`}
                className="flex items-center gap-2 text-slate-400 hover:text-blue-400 text-sm transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span>{companyInfo.contact.phone}</span>
              </a>
              <a
                href={`mailto:${companyInfo.contact.email}`}
                className="flex items-center gap-2 text-slate-400 hover:text-blue-400 text-sm transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>{companyInfo.contact.email}</span>
              </a>
              <p className="text-slate-500 text-sm">{companyInfo.contact.hours}</p>
            </div>

            {/* Social Links */}
            <div className="flex gap-3 mt-4">
              <a
                href={companyInfo.social.facebook}
                className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:bg-blue-600 hover:text-white transition-all"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={companyInfo.social.linkedin}
                className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:bg-blue-600 hover:text-white transition-all"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-slate-500">
              © {new Date().getFullYear()} {companyInfo.legal.company}. Alle Rechte vorbehalten.
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/impressum" className="text-slate-400 hover:text-blue-400 transition-colors">
                Impressum
              </Link>
              <Link to="/datenschutz" className="text-slate-400 hover:text-blue-400 transition-colors">
                Datenschutz
              </Link>
              <Link to="/agb" className="text-slate-400 hover:text-blue-400 transition-colors">
                AGB
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
